import { CalendarMonth, FormatListBulleted } from '@mui/icons-material';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import _ from 'lodash';
import qs from 'qs';
import React, { createContext, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDownloadCsv } from '../../../hooks/useDownloadCsv';
import { useSearchTerm } from '../../../hooks/useSearchTerm';
import { useUserSorts } from '../../../hooks/useUserSorts';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { useGetAllEventTemplate } from '../../../services/react-query/event_templates';
import { EventSortParam } from '../../../services/react-query/events';
import { EventToggleBtnStyling, listPageLayoutStyles } from '../../../styles/sxStyling';
import { EventItemQueryParams } from '../../../types/Event';
import { UserConfigurableModule } from '../../../types/User';
import { EventColumns, useEventFilters } from '../../../utility/event';
import AppLink from '../../common/AppLink/AppLink';
import EventCalendar from '../../common/calenders/EventCalender/EventCalender';
import FilterSidebar from '../../common/filters/FilterSidebar';
import { FilterState } from '../../common/filters/interfaces';
import TemplateModal from '../../common/TemplateModal/TemplateModal';
import EventTable from '../../events/EventTable/EventTable';
import ListHeader from '../../listHeader/ListHeader';
import ListHeaderActions from '../../listHeader/ListHeaderActions';

export type EventsLayoutView = 'table' | 'calendar';

export const FilterContext = createContext<FilterState>(undefined);

const EventsLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const eventListView: EventsLayoutView = _.endsWith(location.pathname, ROUTE_PATHS.EVENTS_TABLE) ? 'table' : 'calendar';
  const { userSorts } = useUserSorts<EventSortParam>(UserConfigurableModule.EVENTS);

  const { data: eventTemplateData } = useGetAllEventTemplate({
    params: { per_page: 0 },
  });
  const eventTemplates = eventTemplateData?.data || [];

  const [showModal, setShowModal] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>(undefined);

  const baseParams = {
    filters: filterState,
    search: useSearchTerm(),
    sort: userSorts,
  };
  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: UserConfigurableModule.EVENTS,
  });

  const filterKey = eventListView === 'table' ? UserConfigurableModule.EVENTS : UserConfigurableModule.EVENTS_CALENDAR;

  const moduleFilters = useEventFilters(eventListView);

  const handleNewEvent = (templateUuid: string | null) => {
    const q: EventItemQueryParams = {};
    if (templateUuid) q.template = templateUuid;
    navigate(`/${ROUTE_PATHS.EVENTS}/new?${qs.stringify(q)}`);
  };

  const headerActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ToggleButtonGroup value={eventListView} exclusive size="small" sx={EventToggleBtnStyling}>
        <AppLink to={`/${ROUTE_PATHS.EVENTS_TABLE}`} sx={{ lineHeight: 0 }}>
          <ToggleButton value="table">
            <FormatListBulleted />
            List
          </ToggleButton>
        </AppLink>

        <AppLink to={`/${ROUTE_PATHS.EVENTS_CALENDAR}`} sx={{ lineHeight: 0 }}>
          <ToggleButton value="calendar">
            <CalendarMonth />
            Calendar
          </ToggleButton>
        </AppLink>
      </ToggleButtonGroup>

      <ListHeaderActions
        module={UserConfigurableModule.EVENTS}
        onAdd={() => setShowModal(true)}
        {...(eventListView === 'table' && {
          isDownloadingCsv,
          onDownloadCsv: handleDownloadCsv,
          availableColumns: EventColumns.map(({ key, label }) => ({ key, label: label ?? key })),
        })}
      />
    </Box>
  );

  return (
    <>
      <Box>
        <ListHeader title="Events" rightActions={headerActions} />

        <Box sx={listPageLayoutStyles}>
          <FilterSidebar moduleFilters={moduleFilters} module={filterKey} activeFilter={filterState} setActiveFilter={setFilterState} />

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <FilterContext.Provider value={filterState}>{eventListView === 'calendar' ? <EventCalendar /> : <EventTable />}</FilterContext.Provider>
          </Box>
        </Box>
      </Box>

      <TemplateModal
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        onCreate={handleNewEvent}
        templates={eventTemplates}
        title="Please choose which event template to use"
      />
    </>
  );
};

export default EventsLayout;
